import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react"; // Removed AlertTriangle
import constant from "../../constant";
import Select from "react-select";
import PartyForm from "../ui/partyform";
import ItemForm from "../ui/itemform";

// Tax Calculation Function (consistent with SaleForm)
// ... (Keep the calculateTaxDetails function as it was in the previous correct version)
const calculateTaxDetails = (items, isInterState) => {
  const taxDetails = {
    cgst: {},
    sgst: {},
    igst: {},
    totalTax: 0,
    grandTotal: 0,
  };

  items.forEach((item) => {
    const taxableAmount = parseFloat(item.itemAmount) || 0;
    const totalAmountWithTax = parseFloat(item.totalAmount) || 0;
    const taxRate = parseFloat(item.taxRate) || 0;
    let taxAmount, splitRate;

    if (isInterState) {
      taxAmount = (taxableAmount * taxRate) / 100;
      taxAmount = Math.round(taxAmount * 100) / 100;
      taxDetails.igst[taxRate] = (taxDetails.igst[taxRate] || 0) + taxAmount;
    } else {
      splitRate = taxRate / 2;
      taxAmount = (taxableAmount * splitRate) / 100;
      taxAmount = Math.round(taxAmount * 100) / 100;
      taxDetails.cgst[splitRate] =
        Math.round(((taxDetails.cgst[splitRate] || 0) + taxAmount) * 100) / 100;
      taxDetails.sgst[splitRate] =
        Math.round(((taxDetails.sgst[splitRate] || 0) + taxAmount) * 100) / 100;
    }

    taxDetails.totalTax += isInterState ? taxAmount : taxAmount * 2;
    taxDetails.grandTotal += totalAmountWithTax;
  });

  taxDetails.totalTax = Math.round(taxDetails.totalTax * 100) / 100;
  taxDetails.grandTotal = Math.round(taxDetails.grandTotal * 100) / 100;

  return taxDetails;
};

const EditSale = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [localError, setLocalError] = useState(null);

  const [billTo, setBillTo] = useState(null);
  const [items, setItems] = useState([]);
  const [invoiceNo, setInvoiceNo] = useState("");
  const [date, setDate] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState("");
  const [additionalCharges, setAdditionalCharges] = useState([]);
  // REMOVED global discount states:
  // const [discount, setDiscount] = useState('');
  // const [discountType, setDiscountType] = useState('%');
  const [amountReceived, setAmountReceived] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [fullyPaid, setFullyPaid] = useState(false);

  const [partyData, setPartyData] = useState([]);
  const [itemData, setItemData] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedBillingAddress, setSelectedBillingAddress] = useState(null);
  const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);
  const [isAddPartyModalOpen, setIsAddPartyModalOpen] = useState(false);
  const [newlyCreatedParty, setNewlyCreatedParty] = useState(null);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [newItemName, setNewItemName] = useState("");
  const [newItemGstRate, setNewItemGstRate] = useState("");
  const [newItemPrice, setNewItemPrice] = useState("");
  const [newItemType, setNewItemType] = useState("");
  const [newItemHsnCode, setNewItemHsnCode] = useState("");
  const [newItemUnit, setNewItemUnit] = useState("");

  const [companyGst, setCompanyGst] = useState("");
  const [companyState, setCompanyState] = useState("");
  const [isInterState, setIsInterState] = useState(false);
  const [taxDetails, setTaxDetails] = useState({
    cgst: {},
    sgst: {},
    igst: {},
    totalTax: 0,
    grandTotal: 0,
  });

  const menuPortalTarget = useRef(document.body);

  // --- Utility Functions ---
  const calculateDueDate = (selectedDate, terms) => {
    if (!selectedDate || !terms || isNaN(parseInt(terms, 10))) return "";
    const dateObj = new Date(selectedDate);
    dateObj.setDate(dateObj.getDate() + parseInt(terms, 10));
    if (isNaN(dateObj.getTime())) return "";
    return dateObj.toISOString().split("T")[0];
  };

  // --- Event Handlers ---
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setDate(newDate);
    setDueDate(calculateDueDate(newDate, paymentTerms));
  };

  const handlePaymentTermsChange = (e) => {
    const newTerms = e.target.value;
    setPaymentTerms(newTerms);
    setDueDate(calculateDueDate(date, newTerms));
  };

  // Reverted simplified logic for fully paid change
  const handleFullyPaidChange = (e) => {
    const isChecked = e.target.checked;
    setFullyPaid(isChecked);
    // Simple logic: If checked, clear amount received/mode. User must re-enter if needed.
    if (isChecked) {
      setAmountReceived("");
      setPaymentMode("");
    }
  };

  // Reverted simplified amount received change handler
  const handleAmountReceivedChange = (e) => {
    setAmountReceived(e.target.value);
    // No validation here against total
    setLocalError(null); // Clear any previous unrelated error
  };

  const handleAddAdditionalCharge = () => {
    setAdditionalCharges([...additionalCharges, { label: "", value: "" }]);
  };

  const handleAdditionalChargeChange = (index, e) => {
    const { name, value } = e.target;
    const updatedCharges = [...additionalCharges];
    updatedCharges[index] = { ...updatedCharges[index], [name]: value };
    setAdditionalCharges(updatedCharges);
  };

  const handleRemoveAdditionalCharge = (index) => {
    const updatedCharges = additionalCharges.filter((_, i) => i !== index);
    setAdditionalCharges(updatedCharges);
  };

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        item: "",
        qty: 1,
        price: "",
        discount: "",
        discountType: "%",
        hsnCode: "",
        unit: "",
        taxRate: "",
        description: "",
        itemAmount: "0.00",
        totalAmount: "0.00",
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    const updatedItems = [...items];
    const currentItem = updatedItems[index];

    if (name === "item") {
      const selectedItemId = value?.value;

      if (selectedItemId === "add_new_item") {
        setIsAddItemModalOpen(true);
        return;
      }

      const selectedItemData = itemData.find(
        (item) => item._id === selectedItemId
      );

      if (selectedItemData) {
        let itemPrice = selectedItemData.isTaxInclusive
          ? Math.round(
              (selectedItemData.price /
                (1 + parseInt(selectedItemData.gstRate) / 100)) *
                100
            ) / 100
          : Math.round(selectedItemData.price * 100) / 100;
        updatedItems[index] = {
          ...currentItem,
          item: selectedItemId,
          hsnCode: selectedItemData.hsnCode || "",
          unit: selectedItemData.unit || "",
          taxRate:
            selectedItemData.gstRate !== undefined
              ? selectedItemData.gstRate.toString()
              : "",
          price: itemPrice || "",
        };
      } else {
        updatedItems[index] = {
          ...currentItem,
          [name]: selectedItemId || "",
          hsnCode: "",
          unit: "",
          taxRate: "",
          price: "",
        };
      }
    } else {
      updatedItems[index] = { ...currentItem, [name]: value };
    }
    setItems(updatedItems);
  };

  // --- Data Fetching (largely unchanged) ---
  const fetchFormData = useCallback(
    async (isInitialLoad = false) => {
      // ... (Keep fetchFormData as it was)
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("No authentication token found");
          return;
        }

        const response = await fetch(
          `${constant.REACT_APP_API_URL}/sales/form-data`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const result = await response.json();
          throw new Error(result.message || "Failed to fetch form data");
        }

        const data = await response.json();

        if (data.success) {
          setPartyData(data.data.partyData || []);
          setItemData(data.data.itemData || []);
          setCompanyGst(data.data.companyGst || "");
          if (isInitialLoad && !invoiceNo)
            setInvoiceNo(data.data.invoiceNumber || "");
          if (isInitialLoad && !termsAndConditions)
            setTermsAndConditions(data.data.terms || "");
        } else {
          throw new Error(data.message || "Failed to fetch form data");
        }
      } catch (fetchError) {
        console.error("Form Data Fetch Error:", fetchError);
        setError(fetchError.message);
      }
    },
    [invoiceNo, termsAndConditions]
  );

  useEffect(() => {
    const fetchSaleData = async () => {
      setLoading(true);
      setError(null);
      setLocalError(null);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("No authentication token found");
        }

        const [saleResponse, formDataResponse] = await Promise.all([
          fetch(`${constant.REACT_APP_API_URL}/sales/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
          fetch(`${constant.REACT_APP_API_URL}/sales/form-data`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
        ]);

        if (!formDataResponse.ok) {
          const result = await formDataResponse.json();
          throw new Error(result.message || "Failed to fetch form data");
        }
        const formDataResult = await formDataResponse.json();
        if (formDataResult.success) {
          setPartyData(formDataResult.data.partyData || []);
          setItemData(formDataResult.data.itemData || []);
          setCompanyGst(formDataResult.data.companyGst || "");
          setCompanyState(formDataResult.data.companyState || "");

        } else {
          throw new Error(
            formDataResult.message || "Failed to process form data"
          );
        }

        if (!saleResponse.ok) {
          const result = await saleResponse.json();
          throw new Error(result.message || "Failed to fetch sales data");
        }
        const saleResult = await saleResponse.json();
        if (saleResult.success && saleResult.data.salesData) {
          const saleData = saleResult.data.salesData;
          // console.log("Fetched Sale Data:", saleData);

          setInitialValues(saleData);

          const currentParty = formDataResult.data.partyData.find(
            (p) => p._id === saleData.partyId
          );
          setBillTo(
            currentParty ? { ...currentParty, _id: saleData.partyId } : null
          );
          setSelectedBillingAddress(
            saleData.partyData?.billingAddress ||
              currentParty?.billingAddress ||
              null
          );
          setSelectedShippingAddress(
            saleData.partyData?.shippingAddress ||
              currentParty?.shippingAddress ||
              null
          );

          setInvoiceNo(saleData.invoiceNumber || "");

          const invoiceDate = saleData.invoiceDate
            ? new Date(saleData.invoiceDate)
            : null;
          const dueDateParsed = saleData.dueDate
            ? new Date(saleData.dueDate)
            : null;
          const formattedInvoiceDate =
            invoiceDate && !isNaN(invoiceDate)
              ? invoiceDate.toISOString().split("T")[0]
              : "";
          const formattedDueDate =
            dueDateParsed && !isNaN(dueDateParsed)
              ? dueDateParsed.toISOString().split("T")[0]
              : "";
          setDate(formattedInvoiceDate);

          if (
            invoiceDate &&
            dueDateParsed &&
            !isNaN(invoiceDate) &&
            !isNaN(dueDateParsed)
          ) {
            const diffTime = Math.abs(dueDateParsed - invoiceDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            setPaymentTerms(diffDays.toString());
          } else {
            setPaymentTerms("");
          }
          setDueDate(formattedDueDate);

          setNotes(saleData.notes || "");
          setTermsAndConditions(
            saleData.terms || formDataResult.data.terms || ""
          );
          setAdditionalCharges(saleData.additionalCharges || []);
          // REMOVED setting global discount state
          // setDiscount(saleData.additionalDiscount?.toString() || '');
          // setDiscountType(saleData.additionalDiscountType || '%');
          setAmountReceived(saleData.amountReceived?.toString() || "");
          setPaymentMode(saleData.paymentMode || "");
          setFullyPaid(saleData.fullyPaid || false);

          setSelectedItemDataForEdit(saleData.items || []);
        } else {
          throw new Error(saleResult.message || "Failed to process sales data");
        }
      } catch (apiError) {
        console.error("Data Fetch Error:", apiError);
        setError(apiError.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSaleData();
  }, [id]);

  useEffect(() => {
    if (newlyCreatedParty) {
      fetchFormData(false);
    }
  }, [newlyCreatedParty, fetchFormData]);

  // --- Tax Calculation Trigger (unchanged) ---
  useEffect(() => {
    const updatedItemsForTax = items.map((item) => {
      // ... (Keep the item mapping logic as it was)
      const selectedItemData = itemData.find((i) => i._id === item.item);
      const price = parseFloat(item.price || 0);
      const qty = parseInt(item.qty || 0);
      const discount = parseFloat(item.discount || 0);
      const discountType = item.discountType || "%";
      const taxRate = parseFloat(
        item.taxRate || selectedItemData?.gstRate || 0
      );

      let itemAmountBeforeDiscount = price * qty;
      let itemDiscountAmountPerUnit = 0;

      if (discountType === "rs") {
        itemDiscountAmountPerUnit = discount;
      } else {
        itemDiscountAmountPerUnit = price * (discount / 100);
      }

      const totalDiscountAmount = itemDiscountAmountPerUnit * qty;
      const calculatedItemAmount =
        itemAmountBeforeDiscount - totalDiscountAmount;
      const taxAmount = calculatedItemAmount * (taxRate / 100);
      const totalAmountWithTax = calculatedItemAmount + taxAmount;

      return {
        ...item,
        itemAmount: calculatedItemAmount.toFixed(2),
        taxRate: taxRate,
        totalAmount: totalAmountWithTax.toFixed(2),
      };
    });
    setTaxDetails(calculateTaxDetails(updatedItemsForTax, isInterState));
  }, [items, isInterState, itemData]);

  // --- InterState Check (unchanged) ---
  useEffect(() => {
    const getIsInterStateVal = () => {
      if (billTo?.billingAddress?.gstNo && companyGst) {
        const partyGstPrefix = billTo.billingAddress.gstNo
          .substring(0, 2)
          .toUpperCase();
        const companyGstPrefix = companyGst.substring(0, 2).toUpperCase();
        setIsInterState(partyGstPrefix !== companyGstPrefix);
      } else {
        setIsInterState(companyState.toLowerCase() !== billTo?.billingAddress?.state.toLowerCase())

        // setIsInterState(false);
      }
    };
    getIsInterStateVal();
  }, [billTo, companyGst]);

  // --- Item Data Setup for Edit (unchanged) ---
  const setSelectedItemDataForEdit = (saleItems) => {
    // ... (Keep setSelectedItemDataForEdit as it was)
    const mappedItems = saleItems.map((fetchedItem) => {
      const itemMaster = itemData.find((i) => i._id === fetchedItem.itemId);

      const quantity = parseInt(fetchedItem.quantity || 1);
      const discount = parseFloat(fetchedItem.discount || 0);
      const discountType = fetchedItem.discountType || "%";
      const itemAmount = parseFloat(fetchedItem.itemAmount || 0);

      let price = 0;
      if (quantity > 0) {
        if (discountType === "%") {
          if (discount < 100) {
            price = itemAmount / (quantity * (1 - discount / 100));
          } else {
            price = 0;
          }
        } else if (discountType === "rs") {
          const discountPerUnit = discount;
          const priceBeforeDiscountPerUnit =
            itemAmount / quantity + discountPerUnit;
          price = priceBeforeDiscountPerUnit;
        } else {
          price = itemAmount / quantity;
        }
      }

      return {
        item: fetchedItem.itemId || "",
        qty: quantity,
        price: isNaN(price) ? "0.00" : price.toFixed(2),
        discount: fetchedItem.discount?.toString() || "",
        discountType: discountType,
        hsnCode: fetchedItem.hsnCode || itemMaster?.hsnCode || "",
        unit: fetchedItem.unit || itemMaster?.unit || "",
        taxRate:
          fetchedItem.taxRate?.toString() ||
          itemMaster?.gstRate?.toString() ||
          "",
        description: fetchedItem.description || "",
        itemAmount: fetchedItem.itemAmount || "0.00",
        totalAmount: fetchedItem.totalAmount || "0.00",
      };
    });
    setItems(mappedItems);
  };

  // const handleBillToChange = () => {
  //     setLocalError("Cannot change the party while editing a sale.");
  //     setTimeout(() => setLocalError(null), 3000);
  // };

  const handleBillToChange = (selectedOption) => {
    if (selectedOption && selectedOption.value === "add_new_party") {
      setIsAddPartyModalOpen(true);
      setBillTo(null);
      setSelectedBillingAddress(null);
      setSelectedShippingAddress(null);
    } else {
      setBillTo(selectedOption ? selectedOption.value : null);
      setSelectedBillingAddress(
        selectedOption && selectedOption.value
          ? selectedOption.value.billingAddress
          : null
      );
      setSelectedShippingAddress(
        selectedOption && selectedOption.value
          ? selectedOption.value.shippingAddress
          : null
      );
    }
  };

  const handleClosePartyModal = () => setIsAddPartyModalOpen(false);
  const handleCloseItemModal = () => {
    setIsAddItemModalOpen(false);
    setNewItemName("");
    setNewItemGstRate("");
    setNewItemPrice("");
    setNewItemType("");
    setNewItemHsnCode("");
    setNewItemUnit("");
    setLocalError(null);
  };
  const handlePartyFormSubmit = async (newPartyData) => {
    // ... (Keep handlePartyFormSubmit as it was)
    setIsAdding(true);
    setLocalError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const response = await fetch(`${constant.REACT_APP_API_URL}/party`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newPartyData),
      });
      const result = await response.json();
      if (!response.ok)
        throw new Error(result.message || "Failed to add party");

      setNewlyCreatedParty(result.data);
      setIsAddPartyModalOpen(false);
      setSuccessMessage("Party added successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (modalError) {
      console.error("Error adding party:", modalError);
      setLocalError(modalError.message);
    } finally {
      setIsAdding(false);
    }
  };
  const handleItemFormSubmit = async (newItemFormData) => {
    // ... (Keep handleItemFormSubmit as it was)
    setIsAdding(true);
    setLocalError(null);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const payload = {
        ...newItemFormData,
        gstRate: newItemFormData.gstRate
          ? parseFloat(newItemFormData.gstRate)
          : 0,
      };

      const response = await fetch(`${constant.REACT_APP_API_URL}/item`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Failed to add item");

      fetchFormData(false);
      handleCloseItemModal();
      setSuccessMessage("Item added successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (modalError) {
      console.error("Error adding item:", modalError);
      setLocalError(modalError.message);
    } finally {
      setIsAdding(false);
    }
  };

  // --- Form Submission Preparation (API Payloads) ---
  const prepareItemsForApi = () => {
    // ... (Keep prepareItemsForApi as it was)
    return items.map((item) => {
      const selectedItemData = itemData.find((i) => i._id === item.item);
      const price = parseFloat(item.price || 0);
      const qty = parseInt(item.qty || 0);
      const discount = parseFloat(item.discount || 0);
      const discountType = item.discountType || "%";
      const taxRate = parseFloat(
        item.taxRate || selectedItemData?.gstRate || 0
      );

      let itemDiscountAmountPerUnit = 0;
      if (discountType === "rs") {
        itemDiscountAmountPerUnit = discount;
      } else {
        itemDiscountAmountPerUnit = price * (discount / 100);
      }

      const totalDiscountAmount = itemDiscountAmountPerUnit * qty;
      const itemTaxableAmount = price * qty - totalDiscountAmount;
      const itemGstAmount = itemTaxableAmount * (taxRate / 100);
      const itemTotalAmount = itemTaxableAmount + itemGstAmount;

      return {
        itemId: item.item,
        itemName: selectedItemData?.name || "Unknown Item",
        quantity: qty,
        price: price,
        discount: discount,
        discountType: discountType,
        itemAmount: itemTaxableAmount.toFixed(2),
        taxRate: taxRate,
        hsnCode: item.hsnCode || selectedItemData?.hsnCode || "",
        unit: item.unit || selectedItemData?.unit || "",
        description: item.description || "",
        taxAmount: itemGstAmount.toFixed(2),
        totalAmount: itemTotalAmount.toFixed(2),
      };
    });
  };

  const preparePayload = (itemsForApi) => {
    const formattedDueDate =
      dueDate && !isNaN(new Date(dueDate))
        ? new Date(dueDate)
            .toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
            .replace(/ /g, "-")
        : null;
    const formattedInvoiceDate =
      date && !isNaN(new Date(date))
        ? new Date(date)
            .toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
            .replace(/ /g, "-")
        : null;

    const calculatedSubtotals = calculateSubtotal();
    const totalAmountNoDecimal = parseFloat(
      calculatedSubtotals.totalAmountNoDecimal
    );
    const roundoffValue =
      parseFloat(calculatedSubtotals.totalAmount) - totalAmountNoDecimal;

    let finalAmountReceived = 0;
    let finalPendingAmount = totalAmountNoDecimal;

    if (fullyPaid) {
      finalAmountReceived = totalAmountNoDecimal;
      finalPendingAmount = 0;
    } else if (amountReceived && parseFloat(amountReceived) >= 0) {
      finalAmountReceived = parseFloat(amountReceived);
      finalPendingAmount = totalAmountNoDecimal - finalAmountReceived;
      finalPendingAmount = Math.max(0, finalPendingAmount);
    }

    return {
      partyId: billTo?._id,
      partyData: {
        name: billTo?.name,
        mobile: billTo?.mobile,
        billingAddress: selectedBillingAddress,
        shippingAddress: selectedShippingAddress,
      },
      items: itemsForApi,
      invoiceDate: formattedInvoiceDate,
      dueDate: formattedDueDate,
      notes: notes || "",
      terms: termsAndConditions || "",
      invoiceNumber: invoiceNo,
      amountReceived: finalAmountReceived.toFixed(2),
      paymentMode: paymentMode || "",
      fullyPaid: fullyPaid,
      additionalCharges: additionalCharges.map((charge) => ({
        label: charge.label,
        value: parseFloat(charge.value || 0),
      })),
      // REMOVED global discount fields:
      // additionalDiscount: parseFloat(discount || 0),
      // additionalDiscountType: discountType || '%',
      totalAmount: parseFloat(calculatedSubtotals.totalAmount).toFixed(2),
      roundoff: roundoffValue.toFixed(2),
      pendingAmount: finalPendingAmount.toFixed(2),
      taxAmount: parseFloat(taxDetails.totalTax).toFixed(2),
    };
  };

  // --- Subtotal Calculation (Global Discount Removed) ---
  const calculateSubtotal = () => {
    let qtyTotal = 0;
    let totalDiscount = 0; // Item discounts only
    let totalTaxableAmount = 0;
    let totalGstAmount = 0;
    let netItemTotal = 0; // Item total including tax

    items.forEach((item) => {
      const price = parseFloat(item.price || 0);
      const qty = parseInt(item.qty || 0);
      const discountVal = parseFloat(item.discount || 0);
      const discountTypeVal = item.discountType || "%";
      const taxRate = parseFloat(item.taxRate || 0);

      qtyTotal += qty;

      let itemDiscountAmountPerUnit = 0;
      if (discountTypeVal === "rs") {
        itemDiscountAmountPerUnit = discountVal;
      } else {
        itemDiscountAmountPerUnit = price * (discountVal / 100);
      }

      const totalItemDiscount = itemDiscountAmountPerUnit * qty;
      totalDiscount += totalItemDiscount;

      const itemTaxableAmount = (price - itemDiscountAmountPerUnit) * qty;
      totalTaxableAmount += itemTaxableAmount;

      const itemGstAmount = itemTaxableAmount * (taxRate / 100);
      totalGstAmount += itemGstAmount;

      netItemTotal += itemTaxableAmount + itemGstAmount;
    });

    let additionalChargesSum = 0;
    additionalCharges.forEach((charge) => {
      additionalChargesSum += parseFloat(charge.value || 0);
    });

    // REMOVED global discount calculation
    // preGlobalDiscountTotal = netItemTotal + additionalChargesSum;
    // globalDiscountAmount calculation removed
    // finalTotalAmount calculation simplified
    const finalTotalAmount = netItemTotal + additionalChargesSum; // Final amount is item total + charges

    return {
      qtyTotal: qtyTotal,
      totalDiscount: totalDiscount.toFixed(2),
      totalTaxableAmount: totalTaxableAmount.toFixed(2),
      totalGstAmount: totalGstAmount.toFixed(2),
      netItemTotal: netItemTotal.toFixed(2),
      additionalChargesSum: additionalChargesSum.toFixed(2),
      // REMOVED globalDiscount field
      // globalDiscount: globalDiscountAmount.toFixed(2),
      totalAmount: finalTotalAmount.toFixed(2),
      totalAmountNoDecimal: Math.round(finalTotalAmount).toFixed(0),
    };
  };

  // --- Form Submission (Update) ---
  const handleUpdateSale = async (e) => {
    e.preventDefault();
    setLocalError(null);
    setIsAdding(true);
    setSuccessMessage("");

    // REMOVED extra validation checks as requested
    // if (!billTo) { ... }
    // if (!date || ...) { ... }
    // if (!dueDate || ...) { ... }
    // if (items.some(...)) { ... }
    // if (!fullyPaid && amountReceived && ...) { ... }

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const itemsForApi = prepareItemsForApi();
      const payload = preparePayload(itemsForApi);
      payload.isInterstate = isInterState;
      // console.log("Payload for API Update:", payload);

      const response = await fetch(
        `${constant.REACT_APP_API_URL}/sales/${id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to update sale");
      }

      // console.log("API Response (Update Sale):", result);
      setSuccessMessage("Sale updated successfully!");
      setTimeout(() => {
        navigate("/sales");
      }, 1500);
    } catch (apiError) {
      console.error("Error updating sale:", apiError);
      setLocalError(apiError.message);
    } finally {
      setIsAdding(false);
    }
  };

  const subtotals = calculateSubtotal();
  const balanceAmount = fullyPaid
    ? "0.00"
    : (
        parseFloat(subtotals.totalAmountNoDecimal) -
        parseFloat(amountReceived || 0)
      ).toFixed(2);

  // --- Options for Select (unchanged) ---
  const partyOptions = partyData.map((party) => ({
    value: party,
    label: party.name,
  }));
  const itemOptions = [
    { value: "add_new_item", label: "+ Add New Item" },
    ...itemData.map((item) => ({
      value: item._id,
      label: item.name,
    })),
  ];

  // --- Navigation ---
  const handleCancel = () => navigate("/sales");
  const handleBack = () => navigate(-1);

  // --- Render Logic ---
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        {/* Simplified error display */}
        <p className="text-red-500">Error: {error}</p>
        <button
          onClick={handleBack}
          className="ml-4 bg-red-500 text-white p-1 rounded text-xs"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    // Reverted: Removed pt-20/md:pt-6, using default padding
    <div className="min-h-screen bg-gray-50 p-6 pt-20 md:pt-6">
      <div className="max-w-7xl mx-auto">
        {/* Reverted: Non-sticky header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h2 className="text-2xl font-bold text-gray-800">Edit Sale</h2>
          </div>
        </div>

        {/* Simplified Messages */}
        {localError && <p className="text-red-500 mb-4">{localError}</p>}
        {successMessage && (
          <p className="text-green-500 mb-4">{successMessage}</p>
        )}

        <form
          onSubmit={handleUpdateSale}
          className="p-6 bg-white shadow-md rounded-md"
        >
          {/* Top Section: Party, Invoice Details */}
          <div className="flex flex-col md:flex-row items-start justify-between w-full mb-6 gap-4">
            {/* Bill To Section */}
            {/* <div className="w-full md:w-3/4 md:pr-4 mb-4 md:mb-0">
                            <label htmlFor="billTo" className="block text-gray-700 text-sm font-bold mb-2">
                                Bill To
                            </label>
                            <div className="shadow-md text-sm p-2 border rounded bg-gray-100">
                                {billTo ? billTo.name : 'Party Not Loaded'}
                            </div>
                            <div className='flex flex-col md:flex-row justify-between gap-2 mt-2'>
                                {selectedBillingAddress ? (
                                    <div className="mt-2 p-3 border rounded text-xs md:text-sm bg-gray-50 w-full">
                                        <h1 className="font-semibold mb-1">Billing Address</h1>
                                        <p>{selectedBillingAddress.name}</p>
                                        <p>{selectedBillingAddress.streetAddress}</p>
                                        <p>{`${selectedBillingAddress.city}, ${selectedBillingAddress.state}, ${selectedBillingAddress.country} - ${selectedBillingAddress.pincode}`}</p>
                                        {selectedBillingAddress.gstNo && <p>GST: {selectedBillingAddress.gstNo}</p>}
                                    </div>
                                ) : <div className="mt-2 p-3 border rounded text-xs md:text-sm bg-gray-100 w-full">Billing address not available.</div>}

                                {selectedShippingAddress ? (
                                    <div className="mt-2 p-2 border rounded text-xs md:text-sm bg-gray-50 w-full">
                                        <h1 className="font-semibold mb-1">Shipping Address</h1>
                                        <p>{selectedShippingAddress.name}</p>
                                        <p>{selectedShippingAddress.streetAddress}</p>
                                        <p>{`${selectedShippingAddress.city}, ${selectedShippingAddress.state}, ${selectedShippingAddress.country} - ${selectedShippingAddress.pincode}`}</p>
                                        {selectedShippingAddress.gstNo && <p>GST: {selectedShippingAddress.gstNo}</p>}
                                    </div>
                                ) : <div className="mt-2 p-3 border rounded text-xs md:text-sm bg-gray-100 w-full">Shipping address not available.</div>}
                            </div>
                        </div> */}

            <div className="w-full md:w-3/4 md:pr-4 mb-4 md:mb-0">
              <label
                htmlFor="billTo"
                className="block text-gray-700 text-sm font-bold mb-2"
              >
                Bill To
              </label>
              <Select
                id="billTo"
                options={partyOptions}
                value={
                  billTo
                    ? partyOptions.find(
                        (option) =>
                          option.value && option.value._id === billTo._id
                      )
                    : null
                }
                onChange={handleBillToChange}
                className="shadow-md text-sm"
                classNamePrefix="react-select"
                placeholder="Search Party or Add New"
                required
                classNameContainer="w-full"
                menuPortalTarget={menuPortalTarget.current}
              />

              <div className="flex flex-col md:flex-row justify-between gap-2 mt-2">
                {/* Display Billing Address */}
                {selectedBillingAddress && (
                  <div className="mt-2 p-3 border rounded text-sm bg-gray-50 w-full">
                    <h1 className="font-semibold mb-1">Billing Address</h1>
                    <p>{selectedBillingAddress.name}</p>
                    <p>{selectedBillingAddress.streetAddress}</p>
                    <p>{`${selectedBillingAddress.city}, ${selectedBillingAddress.state}, ${selectedBillingAddress.country} - ${selectedBillingAddress.pincode}`}</p>
                  </div>
                )}
                {selectedShippingAddress && (
                  <div className="mt-2 p-2 border rounded text-sm bg-gray-50 w-full">
                    <h1 className="font-semibold mb-1">Shipping Address</h1>
                    <p>{selectedShippingAddress.name}</p>
                    <p>{selectedShippingAddress.streetAddress}</p>
                    <p>{`${selectedShippingAddress.city}, ${selectedShippingAddress.state}, ${selectedShippingAddress.country} - ${selectedShippingAddress.pincode}`}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Invoice Details Section */}
            <div className="w-full md:w-1/3">
              <div className="flex flex-col space-y-2 md:space-y-0 md:space-x-4 md:flex-row md:items-center mb-4">
                <div className="mb-4 md:mb-0 flex-1">
                  <label
                    htmlFor="invoiceNoDisplay"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Invoice No
                  </label>
                  <input
                    type="text"
                    id="invoiceNoDisplay"
                    value={invoiceNo}
                    readOnly
                    className="shadow appearance-none text-sm border rounded w-full py-2 px-3 text-gray-700 bg-gray-100 leading-tight focus:outline-none focus:shadow-outline"
                  />
                </div>
                <div className="mb-4 md:mb-0 flex-1">
                  <label
                    htmlFor="date"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Invoice
                  </label>
                  <input
                    type="date"
                    id="date"
                    value={date}
                    onChange={handleDateChange}
                    className="shadow appearance-none border text-sm rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  />
                </div>
              </div>
              <div className="flex flex-col space-y-2 md:space-y-0 md:space-x-4 md:flex-row md:items-center">
                <div className="mb-4 md:mb-0 flex-1">
                  <label
                    htmlFor="paymentTerms"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Payment terms
                  </label>
                  <input
                    type="number" // REMOVED step
                    id="paymentTerms"
                    value={paymentTerms}
                    placeholder="e.g., 30"
                    onChange={handlePaymentTermsChange}
                    className="text-sm shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                    min="0"
                  />
                </div>
                <div className="mb-4 md:mb-0 flex-1">
                  <label
                    htmlFor="dueDate"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Due Date
                  </label>
                  <input
                    type="date"
                    id="dueDate"
                    value={dueDate}
                    className="shadow appearance-none text-sm border rounded w-full py-2 px-3 text-gray-700 bg-gray-100 leading-tight focus:outline-none focus:shadow-outline"
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Items Table - Structure copied from SaleForm */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Item(s)
            </label>
            <div className="overflow-x-auto">
              <table className="min-w-full w-[90vw] table-auto border-collapse border border-gray-200 shadow-md text-xs md:text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    {/* Matched Headers */}
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[20%]">
                      Item
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      HSN/SCN
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      QTY
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      Unit
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      {" "}
                      MRP{" "}
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      Price
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[10%]">
                      Discount
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      Taxable Amt
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      GST(%)
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs font-bold text-gray-700 w-[7%]">
                      Amount
                    </th>
                    <th className="border border-gray-200 px-2 py-2 md:px-2 text-center text-xs font-bold text-gray-700 w-[5%]"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      {/* Item Select + Description */}
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[22%]">
                        <Select
                          id={`item-${index}`}
                          name="item"
                          options={itemOptions}
                          value={
                            itemOptions.find(
                              (option) => option.value === item.item
                            ) || null
                          }
                          onChange={(value) =>
                            handleItemChange(index, {
                              target: { name: "item", value },
                            })
                          }
                          className="shadow-md text-xs"
                          classNamePrefix="react-select"
                          placeholder="Search Item"
                          required
                          menuPortalTarget={menuPortalTarget.current}
                        />
                        <input
                          type="text"
                          id={`description-${index}`}
                          name="description"
                          value={item.description || ""}
                          onChange={(e) => handleItemChange(index, e)}
                          placeholder="Enter description"
                          className="shadow mt-1 appearance-none border rounded w-full py-1 px-2 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs"
                        />
                      </td>
                      {/* HSN (disabled) */}
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[7%]">
                        <input
                          type="text"
                          id={`hsn-${index}`}
                          name="hsnCode"
                          value={item.hsnCode || ""}
                          disabled
                          className="shadow w-[80px] appearance-none border rounded bg-gray-100 py-1 px-1 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs"
                        />
                      </td>
                      {/* QTY */}
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[5%]">
                        <input
                          type="number" // REMOVED step
                          id={`qty-${index}`}
                          name="qty"
                          value={item.qty}
                          onChange={(e) => handleItemChange(index, e)}
                          className="shadow w-[75px] appearance-none border rounded py-1 px-1 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs"
                          required
                          // min={0}
                        />
                      </td>
                      {/* Unit (disabled) */}
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[7%]">
                        <input
                          type="text"
                          id={`unit-${index}`}
                          name="unit"
                          value={item.unit || ""}
                          disabled
                          className="shadow w-[75px] appearance-none border rounded bg-gray-100 py-1 px-1 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs"
                        />
                      </td>
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[5%]">
                        <input
                          type="text"
                          id={`mrp-${index}`}
                          name="mrp"
                          value={Math.round(
                            parseInt(item.price) *
                              parseFloat(1 + parseInt(item.taxRate) / 100)
                          )}
                          // onChange={(e) => handleItemChange(index, e)}
                          className="shadow w-[60px] appearance-none border rounded py-1 px-2 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs" // Reduced padding
                          disabled
                          // min={0}
                        />
                      </td>
                      {/* Price */}
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[7%]">
                        <input
                          type="number" // REMOVED step
                          id={`price-${index}`}
                          name="price"
                          value={item.price}
                          onChange={(e) => handleItemChange(index, e)}
                          className="shadow w-[100px] appearance-none border rounded py-1 px-2 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs"
                          required
                          // min={0}
                        />
                      </td>
                      {/* Discount */}
                      <td className="border border-gray-200 px-0 py-2 md:px-0 text-xs w-[10%]">
                        <div className="flex mx-2 w-[140px]">
                          <input
                            type="number" // REMOVED step
                            id={`discount-${index}`}
                            name="discount"
                            value={item.discount}
                            onChange={(e) => handleItemChange(index, e)}
                            min={0}
                            className="shadow appearance-none border rounded-l w-3/5 py-1 px-2 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs"
                          />
                          <select
                            className="shadow border rounded-r border-l-0 w-2/5 py-1 px-1 md:py-2 md:px-1 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs"
                            name="discountType"
                            value={item.discountType || "%"}
                            onChange={(e) => handleItemChange(index, e)}
                          >
                            <option value="%">%</option>
                            {/* Reverted label */}
                            <option value="rs">rs</option>
                          </select>
                        </div>
                      </td>
                      {/* Taxable Amount (Calculated, Disabled) */}
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[7%]">
                        <input
                          type="text"
                          id={`taxableAmount-${index}`}
                          name="taxableAmount"
                          value={(() => {
                            const priceCalc = parseFloat(item.price || 0);
                            const qtyCalc = parseInt(item.qty || 0);
                            const discountCalc = parseFloat(item.discount || 0);
                            const discountTypeCalc = item.discountType || "%";
                            let itemDiscountAmountPerUnitCalc = 0;
                            if (discountTypeCalc === "rs") {
                              itemDiscountAmountPerUnitCalc = discountCalc;
                            } else {
                              itemDiscountAmountPerUnitCalc =
                                priceCalc * (discountCalc / 100);
                            }
                            const taxableAmountCalc =
                              (priceCalc - itemDiscountAmountPerUnitCalc) *
                              qtyCalc;
                            return isNaN(taxableAmountCalc)
                              ? "0.00"
                              : taxableAmountCalc.toFixed(2);
                          })()}
                          disabled
                          className="w-[100px] shadow appearance-none border rounded bg-gray-100 py-1 px-1 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs text-right"
                        />
                      </td>
                      {/* GST Rate (disabled) */}
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[7%]">
                        <input
                          type="text"
                          id={`taxRate-${index}`}
                          name="taxRate"
                          value={item.taxRate || ""}
                          disabled
                          className="shadow w-[50px] appearance-none border rounded bg-gray-100 py-1 px-1 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs text-right"
                        />
                      </td>
                      {/* Final Amount (Calculated, Disabled) */}
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-xs w-[10%]">
                        <input
                          type="text"
                          id={`amount-${index}`}
                          name="amount"
                          value={(() => {
                            const priceCalc = parseFloat(item.price || 0);
                            const qtyCalc = parseInt(item.qty || 0);
                            const discountCalc = parseFloat(item.discount || 0);
                            const discountTypeCalc = item.discountType || "%";
                            const taxRateCalc = parseFloat(item.taxRate || 0);
                            let itemDiscountAmountPerUnitCalc = 0;
                            if (discountTypeCalc === "rs") {
                              itemDiscountAmountPerUnitCalc = discountCalc;
                            } else {
                              itemDiscountAmountPerUnitCalc =
                                priceCalc * (discountCalc / 100);
                            }
                            const taxableAmountCalc =
                              (priceCalc - itemDiscountAmountPerUnitCalc) *
                              qtyCalc;
                            const gstAmountCalc =
                              taxableAmountCalc * (taxRateCalc / 100);
                            const finalAmountCalc =
                              taxableAmountCalc + gstAmountCalc;
                            return isNaN(finalAmountCalc)
                              ? "0.00"
                              : finalAmountCalc.toFixed(2);
                          })()}
                          disabled
                          className="shadow w-[100px] appearance-none border rounded bg-gray-100 py-1 px-2 md:py-2 md:px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs text-right"
                        />
                      </td>
                      {/* Remove Button */}
                      <td className="border border-gray-200 px-2 py-2 md:px-2 text-center w-[5%]">
                        {items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className="text-red-600 hover:text-red-800"
                            title="Remove Item"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {/* Footer Row - Updated Structure */}
                  <tr className="font-semibold bg-gray-50">
                    <td
                      colSpan="2"
                      className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs"
                    >
                      Items Total
                    </td>
                    <td className="border border-gray-200 px-2 py-2 md:px-2 text-left text-xs">
                      {subtotals.qtyTotal}
                    </td>
                    <td></td>
                    <td></td>
                    <td></td> {/* Spacers */}
                    <td className="border border-gray-200 px-2 py-2 md:px-2 text-right text-xs">
                      {subtotals.totalDiscount}
                    </td>
                    <td className="border border-gray-200 px-2 py-2 md:px-2 text-right text-xs">
                      {subtotals.totalTaxableAmount}
                    </td>
                    <td className="border border-gray-200 px-2 py-2 md:px-2 text-right text-xs">
                      {subtotals.totalGstAmount}
                    </td>
                    <td className="border border-gray-200 px-2 py-2 md:px-2 text-right text-xs">
                      {subtotals.netItemTotal}
                    </td>
                    <td></td>
                    {/* Spacer */}
                  </tr>
                </tbody>
              </table>
            </div>
            {/* Add Item Button */}
            <button
              type="button"
              onClick={handleAddItem}
              className="flex items-center gap-2 text-sm text-black font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline mt-2"
            >
              <Plus size={16} /> Add Item
            </button>
          </div>

          {/* Bottom Section: Notes, Terms, Summary */}
          <div className="flex flex-col md:flex-row justify-between gap-6">
            {/* Left Side: Notes, Terms, Additional Charges */}
            <div className="w-full md:w-1/2 space-y-4">
              {/* Additional Charges */}
              {/* <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-bold mb-2">Additional Charges <span className='text-xs font-normal text-gray-500'>(optional)</span> </label>
                                {additionalCharges.map((charge, index) => (
                                    <div key={index} className="flex items-center mb-2 gap-2">
                                        <input
                                            type="text" name="label" value={charge.label} onChange={(e) => handleAdditionalChargeChange(index, e)}
                                            placeholder="Charge Label (e.g., Shipping)"
                                            className="shadow appearance-none border rounded w-1/2 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs"
                                        />
                                        <input
                                            type="number" // REMOVED step
                                            name="value" value={charge.value} onChange={(e) => handleAdditionalChargeChange(index, e)}
                                            placeholder="Amount"
                                            className="shadow appearance-none border rounded w-1/2 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline text-xs"
                                        />
                                        <button type="button" onClick={() => handleRemoveAdditionalCharge(index)} className="text-red-600 hover:text-red-800 px-2" title="Remove Charge">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                                <button type="button" onClick={handleAddAdditionalCharge} className="text-sm text-blue-600 hover:text-blue-800 font-semibold py-1 px-1 rounded focus:outline-none focus:shadow-outline flex items-center gap-1">
                                     <Plus size={14} /> Add Charge
                                </button>
                            </div> */}
              {/* Notes */}
              <div className="mb-4">
                <label
                  htmlFor="notes"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Notes{" "}
                  <span className="text-xs font-normal text-gray-500">
                    (optional)
                  </span>
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  placeholder="Enter internal notes if any..."
                  onChange={(e) => setNotes(e.target.value)}
                  rows="4"
                  className="shadow appearance-none border text-sm rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
              {/* Terms */}
              <div className="mb-4">
                <label
                  htmlFor="termsAndConditions"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Terms and Conditions{" "}
                  <span className="text-xs font-normal text-gray-500">
                    (optional)
                  </span>
                </label>
                <textarea
                  id="termsAndConditions"
                  value={termsAndConditions}
                  placeholder="Enter terms and conditions for the customer..."
                  rows="4"
                  onChange={(e) => setTermsAndConditions(e.target.value)}
                  className="shadow appearance-none border text-sm rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
            </div>

            {/* Right Side: Payment, Summary */}
            <div className="w-full md:w-1/2 space-y-4">
              {/* Amount Received & Payment Mode & Mark Fully Paid */}

              {/* <div className="mb-4 flex items-center">
                                <input
                                    type="checkbox" id="fullyPaid" checked={fullyPaid} onChange={handleFullyPaidChange}
                                    className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="fullyPaid" className="text-gray-700 text-sm font-bold">
                                    Mark as Fully Paid
                                </label>
                            </div>

                            <div className='flex flex-col md:flex-row gap-4'>
                                <div className="flex-1">
                                    <label htmlFor="amountReceived" className="block text-gray-700 text-sm font-bold mb-2">
                                        Amount Received
                                    </label>
                                    <input
                                        type="number" // REMOVED step
                                        id="amountReceived" placeholder='0.00'
                                        value={fullyPaid ? subtotals.totalAmountNoDecimal : amountReceived}
                                        onChange={handleAmountReceivedChange}
                                        className={`shadow appearance-none border text-sm rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${fullyPaid ? 'bg-gray-100' : ''}`}
                                        min={0} disabled={fullyPaid}
                                    />
                                </div>
                                <div className="flex-1">
                                    <label htmlFor="paymentMode" className="block text-gray-700 text-sm font-bold mb-2">
                                        Payment Mode
                                    </label>
                                    <select
                                        id="paymentMode" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}
                                        className="shadow border text-sm rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                    // required={!fullyPaid && parseFloat(amountReceived || 0) > 0}
                                    >
                                        <option value="">Select Mode</option>
                                        <option value="cash">Cash</option>
                                        <option value="cheque">Cheque</option>
                                        <option value="bank transfer">Bank Transfer</option>
                                        <option value="upi">UPI</option>
                                        <option value="card">Card</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                            </div> */}

              {/* Summary Section - Reverted Simple Layout */}
              <div className="my-4 border-t pt-4">
                {/* <h3 className="text-lg font-semibold mb-3 text-gray-800">Summary</h3> */}
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Taxable Amount:
                    </label>
                    <span>{subtotals.totalTaxableAmount}</span>
                  </div>

                  <h3 className="text-sm font-bold mb-2">
                    Tax Summary ({isInterState ? "Inter-State" : "Intra-State"})
                  </h3>

                  {/* Simplified Tax Display */}
                  {taxDetails.totalTax > 0 && (
                    <div className=" my-1 py-1">
                      {Object.entries(taxDetails.cgst).map(
                        ([rate, amount]) =>
                          amount > 0 && (
                            <div
                              key={`cgst-${rate}`}
                              className="flex justify-between text-sm"
                            >
                              <span className="">CGST @ {rate}%:</span>
                              <span>{amount.toFixed(2)}</span>
                            </div>
                          )
                      )}
                      {Object.entries(taxDetails.sgst).map(
                        ([rate, amount]) =>
                          amount > 0 && (
                            <div
                              key={`sgst-${rate}`}
                              className="flex justify-between text-sm"
                            >
                              <span className="">SGST @ {rate}%:</span>
                              <span>{amount.toFixed(2)}</span>
                            </div>
                          )
                      )}
                      {Object.entries(taxDetails.igst).map(
                        ([rate, amount]) =>
                          amount > 0 && (
                            <div
                              key={`igst-${rate}`}
                              className="flex justify-between text-sm"
                            >
                              <span className="">IGST @ {rate}%:</span>
                              <span>{amount.toFixed(2)}</span>
                            </div>
                          )
                      )}
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-sm">
                    <span>Total Tax:</span>
                    <span>{taxDetails.totalTax.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex justify-between">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Total Amount:
                    </label>
                    <span className="text-sm">{subtotals.totalAmount}</span>
                  </div>
                  <div className="flex justify-between">
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      Total Amount:(roundoff)
                    </label>
                    <span className="text-sm font-semibold">
                      {" "}
                      {subtotals.totalAmountNoDecimal}{" "}
                    </span>
                  </div>

                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded focus:outline-none focus:shadow-outline text-sm ${
                isAdding ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={isAdding}
            >
              {isAdding ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                  Updating...
                </>
              ) : (
                "Update Sale"
              )}
            </button>
          </div>

          {/* Modals (Unchanged structure) */}
          {isAddPartyModalOpen && (
            <div
              className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center"
              onClick={handleClosePartyModal}
            >
              <div
                className="relative p-5 border w-full md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                  onClick={handleClosePartyModal}
                >
                  ×
                </button>
                <h3 className="text-lg font-bold mb-4">Add New Party</h3>
                {localError && (
                  <p className="text-red-500 mb-4">{localError}</p>
                )}
                <PartyForm
                  onSubmit={handlePartyFormSubmit}
                  onCancel={handleClosePartyModal}
                />
              </div>
            </div>
          )}
          {isAddItemModalOpen && (
            <div
              className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center"
              onClick={handleCloseItemModal}
            >
              <div
                className="relative p-5 border w-full md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                  onClick={handleCloseItemModal}
                >
                  ×
                </button>
                <h3 className="text-lg font-bold mb-4">Add New Item</h3>
                {localError && (
                  <p className="text-red-500 mb-4">{localError}</p>
                )}
                <ItemForm
                  name={newItemName}
                  setName={setNewItemName}
                  gstRate={newItemGstRate}
                  setGstRate={setNewItemGstRate}
                  price={newItemPrice}
                  setPrice={setNewItemPrice}
                  itemType={newItemType}
                  setItemType={setNewItemType}
                  hsnCode={newItemHsnCode}
                  setHsnCode={setNewItemHsnCode}
                  unit={newItemUnit}
                  setUnit={setNewItemUnit}
                  loading={isAdding}
                  isEditing={false}
                  onSubmit={handleItemFormSubmit}
                  onCancel={handleCloseItemModal}
                  error={localError}
                />
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default EditSale;
